/**
 * Model info in het fan-paneel (16-10-2026, Jordy), de LINKERKOLOM naast Fan
 * info (18-10-2026).
 *
 *   ModelKop         vast bovenaan: avatar, naam, Persona guide, Do's / Don'ts.
 *   ModelInfoSectie  het scrollende midden: nette balken AFGELEID uit de
 *                    persona-gids (lib/modelInfo.ts modelBalken), dan notities,
 *                    eigen velden en Add model info.
 *
 * 19-10-2026 (Jordy): ALLE model-velden zijn inline bewerkbaar zoals de FAN-kolom
 * (klik, aanpassen, blur/enter = opslaan), rechtstreeks naar model_info:
 * Do's/Don'ts, notities, eigen velden en de interne velden plaats + land. De
 * balken komen uit de persona-gids en zijn hier alleen-lezen; die bewerk je in
 * het aparte Persona guide-scherm (geen potlood meer, niets opent hier de gids).
 * Chatters bewerken hun eigen modellen; de server controleert.
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { useApi, usePoll } from '../lib/useApi'
import {
  antwoordUit, doDont, doDontOpslag, metEngels, modelBalken, regelsEngels, modelNieuwVeld, modelOpslag, modelRegels, modelVeldWeg,
  plaatsLand, plaatsLandOpslag,
} from '../lib/modelInfo'
import type { Balk, ModelInfoEngels } from '../lib/modelInfo'
import type { Info, Opslag, Regel } from '../lib/fanInfo'
import { Avatar } from './ui'
import { StandTekst, Veld } from './InlineVeld'
import type { Stand } from './InlineVeld'

interface Gids { persona_id: number; tekst: string; status: 'draft' | 'confirmed' | null; kan_bewerken: boolean }

/**
 * 18-10-2026 (Jordy): de Engelse weergave van Model info, één keer per model
 * opgehaald; zolang de server nog vertaalt (klaar=false) elke 5 s opnieuw.
 */
export function useModelInfoEngels(personaId: number | null | undefined): ModelInfoEngels | null {
  const q = useApi<ModelInfoEngels>(personaId ? `/api/admin/models/personas/${personaId}/model-info-engels` : null,
                                    [personaId])
  usePoll(5000, q.refetch, !!personaId && !!q.data && !q.data.klaar)
  return q.data
}

/** Een lijst als nette bullets; leeg = "—". */
function Bullets({ regels }: { regels: string[] }) {
  if (!regels.length) return <span className="mut2">—</span>
  return <ul className="mlijst">{regels.map((r, n) => <li key={n}>{r}</li>)}</ul>
}

/**
 * 18-10-2026 (Jordy): Do's en Don'ts ONDER elkaar, elk over de volle breedte als
 * bullets (in het Engels zodra vertaald). Bewerken gaat direct in het veld en
 * slaat op via de bestaande profielroute.
 */
export function ModelDoDont({ personaId, info, engels, onSaved }: {
  personaId: number | null | undefined
  info: Info | null | undefined
  engels?: ModelInfoEngels | null
  onSaved: () => void
}) {
  const dd = doDont(info)
  if (!personaId) return null
  const sla = (sleutel: 'dos' | 'donts') => async (w: string) => {
    const o = doDontOpslag(personaId, sleutel, w)
    await api.patch(o.pad, o.body)
    onSaved()
  }
  // 20-10-2026 (Jordy): precies zoals een fan-veld: altijd een invoerveld,
  // klikken = meteen typen, opslaan bij verlaten. Geen aparte bewerkmodus.
  // Staat er een Engelse vertaling die afwijkt van de opgeslagen tekst, dan
  // die eronder ter info (alleen-lezen).
  const blok = (sleutel: 'dos' | 'donts', label: string, cls: string) => {
    const en = regelsEngels(dd[sleutel], engels?.[sleutel], engels?.klaar)
    const anders = en.join('\n') !== regelsEngels(dd[sleutel], undefined, false).join('\n')
    return (
      <div className={cls} data-testid={`model-${sleutel}`}>
        <Veld label={label} waarde={dd[sleutel]} meerregelig testid={`mveld-${sleutel}`} opslaan={sla(sleutel)} />
        {anders ? (
          <div className="mut2" data-testid={`model-${sleutel}-engels`} style={{ fontSize: 11.5, marginTop: 2 }}>
            In English: <Bullets regels={en} />
          </div>
        ) : null}
      </div>
    )
  }
  return (
    <div className="dodont" data-testid="model-dodont">
      {blok('dos', "Do's", 'modeldo')}
      {blok('donts', "Don'ts", 'modeldont')}
    </div>
  )
}

/**
 * 18-10-2026 (Jordy): Important notes in het modelblok, onder Do's/Don'ts en boven
 * de balken. Eén notitie (gespreksniveau): dezelfde als de gele balk boven de
 * chat, zelfde route; na opslaan ververst het gesprek en dus beide plekken.
 * Ingevuld = geel (zelfde geel als die balk); leeg = neutraal met "+ add".
 */
export function ImportantNotes({ conversationId, notities, focusSleutel, onSaved }: {
  conversationId: number | string
  notities: string | null | undefined
  /** Ophogen = openen en de cursor erin (vanuit de notitiebalk boven de chat). */
  focusSleutel?: number
  onSaved: () => void
}) {
  const tekst = String(notities ?? '').trim()
  const [open, setOpen] = useState(false)
  const eerste = useRef(focusSleutel)
  useEffect(() => {
    if (focusSleutel && focusSleutel !== eerste.current) setOpen(true)
  }, [focusSleutel])
  const sla = async (w: string) => {
    await api.patch(`/api/admin/conversations/${conversationId}/important-notes`, { notes: w })
    onSaved()
  }
  if (open) {
    return (
      <div className="mnotitie bewerken" data-testid="model-notes">
        <Veld label="Important notes" waarde={tekst} meerregelig testid="veld-notes" opslaan={sla}
              focusSleutel={focusSleutel || 1}
              extra={<button type="button" className="linkbtn" onClick={() => setOpen(false)}>Close</button>} />
      </div>
    )
  }
  return tekst ? (
    <div className="mnotitie gevuld" data-testid="model-notes" title="Important notes — shown above the chat too">
      <span className="ic" aria-hidden="true">⚠</span>
      <div className="tekst"><b>Important notes</b><div>{tekst}</div></div>
      <button type="button" className="bewerk" aria-label="Edit important notes" onClick={() => setOpen(true)}>✎</button>
    </div>
  ) : (
    <button type="button" className="mnotitie leeg" data-testid="model-notes" onClick={() => setOpen(true)}>
      <span aria-hidden="true">＋</span> add important notes
    </button>
  )
}

/** Vast bovenaan de modelkolom: wie en de Persona guide. */
export function ModelKop({ naam, avatarUrl, onGids }: {
  naam: string
  avatarUrl?: string | null
  onGids?: () => void
}) {
  return (
    <div data-testid="model-kop">
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <Avatar name={naam} src={avatarUrl ?? undefined} size={24} />
        <b style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{naam}</b>
        <span className="spacer" style={{ flex: 1 }} />
        {onGids ? <button className="btn ghost sm" type="button" onClick={onGids}>Persona guide</button> : null}
      </div>
    </div>
  )
}

/**
 * 18-10-2026 (Jordy): bovenaan het scrollende midden van de modelkolom: Do's en
 * Don'ts (onder elkaar, volle breedte), boven de balken. Niet in de vaste
 * bovenkant: volledige lijsten pasten daar niet en werden afgekapt.
 * Important notes zijn FAN-notities en staan in de fan-kolom, onder Status.
 */
export function ModelBovenblok({ personaId, info, engels, onSaved }: {
  personaId: number | null | undefined
  info: Info | null | undefined
  engels?: ModelInfoEngels | null
  onSaved: () => void
}) {
  return (
    <div className="modelbovenblok" data-testid="model-bovenblok">
      <ModelDoDont personaId={personaId} info={info} engels={engels} onSaved={onSaved} />
    </div>
  )
}

/** Eén balk: label links, de opgeschoonde inhoud rechts; owner/admin een potlood. */
function BalkRij({ b, engels, kanBewerken, opslaan, onAntwoord }: {
  b: Balk
  engels?: ModelInfoEngels | null
  kanBewerken: boolean
  opslaan: (tekst: string) => Promise<void>
  /** Standard answers: klik = in het berichtvak (Engels + origineel; de server kiest de taal). */
  onAntwoord?: (engels: string, origineel: string) => void
}) {
  const [open, setOpen] = useState(false)
  const origineel = b.items
  // 18-10-2026: in het Engels zodra de vertaling klaar is (anders het origineel).
  b = { ...b, items: metEngels(b, engels) }
  const invoegbaar = !!onAntwoord && b.sleutel === 'vaste antwoorden'
  // Klikbare Standard answers altijd als lijst (anders geen knoppen).
  const kort = !invoegbaar && b.items.length <= 4 && !b.items.some((i) => i.kop)
    && b.items.reduce((n, i) => n + i.tekst.length, 0) <= 120
  const regel = (t: string) => {
    // Vaste antwoorden: "vraag — antwoord", de vraag vet.
    const n = t.indexOf(' — ')
    return n > 0 ? <><b>{t.slice(0, n)}</b>{t.slice(n)}</> : t
  }
  return (
    <div className="mbalk" data-testid={`mbalk-${b.sleutel}`}>
      <span className="lbl">{b.label}</span>
      <div className="wrd">
        {!b.items.length ? <span className="mut2">—</span>
          : kort ? b.items.map((i) => i.tekst).join(' · ')
          : (
            <ul>
              {b.items.map((i, n) => i.kop
                ? <li key={n} className="sub">{i.tekst}</li>
                : invoegbaar && i.tekst.includes(' — ') ? (
                  // 18-10-2026 (Jordy): klikbaar; zet het antwoord in het berichtvak,
                  // in de taal van dit gesprek. Er wordt niets verstuurd.
                  <li key={n}>
                    <button type="button" className="antwoordknop" data-testid="standaard-antwoord"
                            title="Put this answer in the message box (in this chat's language). Nothing is sent."
                            onClick={() => onAntwoord?.(antwoordUit(i.tekst, 'en'),
                                                        antwoordUit(origineel[n]?.tekst ?? '', 'nl'))}>
                      {regel(i.tekst)} <span className="mut2">↵ Insert</span>
                    </button>
                  </li>
                ) : <li key={n}>{regel(i.tekst)}</li>)}
            </ul>
          )}
      </div>
      {kanBewerken && b.kop && !open ? (
        <button type="button" className="bewerk" title="Edit this section of the persona guide"
                aria-label={`Edit ${b.label}`} data-testid={`bewerk-${b.sleutel}`}
                onClick={() => setOpen(true)}>✎</button>
      ) : <span />}
      {/* Owner/admin: de ruwe gidssectie, over de volle breedte van de balk. */}
      {open && b.kop ? (
        <div className="gidsbewerk">
          <Veld label={`Persona guide · ${b.label}`} waarde={b.rauw} meerregelig
                testid={`gids-${b.sleutel}`} opslaan={opslaan}
                extra={<button type="button" className="linkbtn" onClick={() => setOpen(false)}>Close</button>} />
        </div>
      ) : null}
    </div>
  )
}

export function ModelInfoSectie({ personaId, info, notities, engels, onSaved, onAntwoord }: {
  personaId: number | null | undefined
  info: Info | null | undefined
  engels?: ModelInfoEngels | null
  /** 18-10-2026: een Standard answer in het berichtvak zetten. */
  onAntwoord?: (engels: string, origineel: string) => void
  notities: string | null | undefined
  onSaved: () => void
}) {
  const g = useApi<Gids>(personaId ? `/api/admin/models/personas/${personaId}/persona-gids` : null, [personaId])
  const [nieuwLabel, setNieuwLabel] = useState('')
  const [nieuwWaarde, setNieuwWaarde] = useState('')
  const [nieuwStand, setNieuwStand] = useState<Stand>('')
  const [nieuwFout, setNieuwFout] = useState('')

  if (!personaId) {
    return <div className="mut2" style={{ fontSize: 12 }} data-testid="model-info-sectie">This model has no profile yet.</div>
  }
  const stuur = async (o: Opslag) => {
    await api.patch(o.pad, o.body)
    onSaved()
  }
  const balken = g.data ? modelBalken(g.data.tekst) : []
  // 19-10-2026 (Jordy): de balken (persona-gids) zijn hier alleen-lezen.
  const kanBewerken = false
  const pl = plaatsLand(info)
  const regels = modelRegels(info, notities)
  const sla = (r: Regel) => (w: string) => stuur(modelOpslag(personaId, info, r, w))
  const voegToe = async () => {
    const o = modelNieuwVeld(personaId, info, nieuwLabel, nieuwWaarde)
    if (!o) { setNieuwStand('error'); setNieuwFout('Give the field a name.'); return }
    setNieuwStand('saving'); setNieuwFout('')
    try {
      await stuur(o)
      setNieuwLabel(''); setNieuwWaarde(''); setNieuwStand('saved')
    } catch (e) {
      setNieuwStand('error'); setNieuwFout(String((e as Error)?.message || e))
    }
  }

  // 19-10-2026 (Jordy): INTERNE velden. Nooit aan een fan: die hoort alleen
  // "somewhere in the Netherlands". De server houdt ze uit fan-antwoorden.
  // 20-10-2026 (Jordy): direct onder Location (zonder scrollen zichtbaar).
  const plaatsBlok = (
      <div data-testid="model-plaats-land" style={{ display: 'grid', gap: 6, marginTop: 6, marginBottom: 6 }}>
        <div className="fanvast-rij">
          <Veld label="City (internal)" waarde={pl.plaats} testid="mveld-plaats"
                opslaan={(w) => stuur(plaatsLandOpslag(personaId, 'plaats', w))} />
          <Veld label="Country (internal)" waarde={pl.land} testid="mveld-land"
                opslaan={(w) => stuur(plaatsLandOpslag(personaId, 'land', w))} />
        </div>
        <div className="mut2" style={{ fontSize: 10.5 }}>
          Internal only — never tell a fan the city. Say “somewhere in the Netherlands”.
        </div>
      </div>
  )

  return (
    <div data-testid="model-info-sectie">
      {g.data?.status === 'draft' ? (
        <div className="mut2" style={{ fontSize: 11.5, marginBottom: 6 }}>Draft — not yet confirmed by the model.</div>
      ) : null}

      <div className="mbalken" data-testid="model-balken">
        {g.loading && !g.data ? <div className="mut2" style={{ fontSize: 12 }}>Loading persona guide…</div> : null}
        {g.data && !g.data.tekst?.trim() ? (
          <div className="mut2" style={{ fontSize: 12 }}>No persona guide yet.</div>
        ) : null}
        {!(g.data?.tekst?.trim() && balken.some((b) => b.sleutel === 'woonplaats')) ? plaatsBlok : null}
        {g.data?.tekst?.trim() ? balken.map((b) => (
          <Fragment key={b.sleutel}>
            <BalkRij b={b} engels={engels} kanBewerken={kanBewerken} onAntwoord={onAntwoord}
                     opslaan={async () => {}} />
            {b.sleutel === 'woonplaats' ? plaatsBlok : null}
          </Fragment>
        )) : null}
      </div>


      <div data-testid="model-info-overig" style={{ display: 'grid', gap: 6, marginTop: 8 }}>
        {regels.map((r) => (
          <Veld key={r.id} label={r.label} waarde={r.waarde} meerregelig={r.meerregelig}
                testid={`mveld-${r.id}`} opslaan={sla(r)}
                extra={r.soort === 'eigen' ? (
                  <button type="button" className="linkbtn" title="Remove this custom field"
                          onClick={() => { void stuur(modelVeldWeg(personaId, info, Number(r.sleutel))) }}>
                    ✕
                  </button>
                ) : undefined} />
        ))}
      </div>
      <div className="fanveld" data-testid="model-info-toevoegen" style={{ marginTop: 8 }}>
        <div className="row" style={{ alignItems: 'center', gap: 6 }}>
          <span className="mut2" style={{ fontSize: 11.5 }}>Add model info</span>
          <span style={{ flex: 1 }} />
          <StandTekst stand={nieuwStand} fout={nieuwFout} />
        </div>
        <div className="row" style={{ gap: 6 }}>
          <input className="inp" placeholder="Field" value={nieuwLabel}
                 aria-label="New model field name" onChange={(e) => setNieuwLabel(e.target.value)} />
          <input className="inp" placeholder="Value" value={nieuwWaarde} aria-label="New model field value"
                 onChange={(e) => setNieuwWaarde(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void voegToe() }} />
          <button className="btn ghost sm" type="button" onClick={() => void voegToe()}>Add</button>
        </div>
      </div>
      <div className="mut2" style={{ fontSize: 11, marginTop: 6 }}>Model changes apply to this model in every chat.</div>
    </div>
  )
}
